import React, { useState, useEffect, useRef } from 'react';
import { loadBalance } from '../../../api/cardService';
import axios from 'axios';
import { FaCreditCard, FaCcVisa, FaCheckCircle, FaTimesCircle, FaLock, FaArrowLeft } from 'react-icons/fa';

const CardBalanceLoad = () => {
    // Basic Form State
    const [cardNumber, setCardNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Payment State
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentData, setPaymentData] = useState({
        cardName: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
    });
    const [paymentErrors, setPaymentErrors] = useState({});
    const [paymentHtml, setPaymentHtml] = useState(null);
    const [transactionId, setTransactionId] = useState(null); 
    
    // Result State
    const [paymentOutcome, setPaymentOutcome] = useState(null); // 'success' | 'failed'
    const [resultMessage, setResultMessage] = useState('');

    const iframeRef = useRef(null);

    // Helpers
    const detectCardType = (number) => {
        const re = {
            1: /^4/,
            2: /^5[1-5]/,
        };
        if (re[1].test(number)) return '1'; // Visa
        if (re[2].test(number)) return '2'; // MasterCard
        return null;
    };

    // Payment Result Listener
    useEffect(() => {
        const handleMessage = (event) => {
            // Origin check logic similar to SportsAppointment
            // Simplify for this component
            const data = event.data;
            if (!data || data.type !== 'PAYMENT_RESULT') return;

            if (data.status === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart bakiyesi başarıyla yüklendi.');
                setPaymentHtml(null);
            } else if (data.status === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Iframe Load Handler (Fallback similar to SportsAppointment)
    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        try {
            const href = iframe.contentWindow?.location?.href;
            if (!href) return;
            
            // Check if returned to our app
            const iframeUrl = new URL(href);
            // Basic check if it contains our success/fail paths or query params
            const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
            const error = iframeUrl.searchParams.get('error');

            if (paymentStatus === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart bakiyesi başarıyla yüklendi.');
                setPaymentHtml(null);
            } else if (paymentStatus === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        } catch (err) {
            // CORS error expected when on bank page
        }
    };


    // Step 1: Initialize Load Transaction & Switch to Payment Form
    const handleInitiate = async () => {
        if (!cardNumber || !amount) {
            alert("Lütfen kart numarası ve tutar giriniz.");
            return;
        }

        setLoading(true);
        try {
            const res = await loadBalance({ cardNumber, amount: parseFloat(amount) });
            
            if (res.isSuccess) {
                setTransactionId(res.data.transactionId);
                setShowPaymentForm(true);
                setPaymentOutcome(null); // Reset result
                setPaymentHtml(null);
                setPaymentData({
                    cardName: '',
                    cardNumber: '',
                    expiryMonth: '',
                    expiryYear: '',
                    cvv: ''
                });
            } else {
                 alert(res.resultMessage || "İşlem başlatılamadı.");
            }
        } catch (error) {
            console.error(error);
            alert("Hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Submit Payment to Backend
    const handlePaymentSubmit = async () => {
        const errors = {};
        if (!paymentData.cardName) errors.cardName = 'Kart üzerindeki isim gereklidir';
        if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 16) errors.cardNumber = 'Geçerli bir kart numarası giriniz';
        if (!paymentData.expiryMonth || !paymentData.expiryYear) errors.expiry = 'Son kullanma tarihi seçiniz';
        if (!paymentData.cvv || paymentData.cvv.length < 3) errors.cvv = 'CVV gereklidir';

        if (Object.keys(errors).length > 0) {
            setPaymentErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'https://panel.iskenderun.bel.tr/api';
            // Clean Card Number
            const cleanCardNumber = paymentData.cardNumber.replace(/\s/g, '');
            
            const payload = {
                amount: parseFloat(amount),
                paymentGroup: "CardTopUp",
                referenceId: transactionId.toString(),
                description: `Kart Yukleme Ref:${transactionId}`,
                callbackUrl: window.location.origin + "/hizmet-rehberi/spor-randevu/balance-callback",
                pan: cleanCardNumber,
                cv2: paymentData.cvv,
                expireMonth: paymentData.expiryMonth,
                expireYear: paymentData.expiryYear,
                cardType: detectCardType(cleanCardNumber) || "1",
                firstName: paymentData.cardName.split(' ')[0],
                lastName: paymentData.cardName.split(' ').slice(1).join(' ') || 'User',
                email: 'info@iskenderun.bel.tr'
            };

            const paymentRes = await axios.post(`${baseUrl}/payment/initiate-general`, payload);

            if (paymentRes.data?.isSuccess) {
                 const htmlContent = paymentRes.data.data.html || paymentRes.data.data;
                 setPaymentHtml(htmlContent);
            } else {
                // Show Error UI instead of alert
                setPaymentOutcome('failed');
                const paymentResultMessage = paymentRes && paymentRes.data ? paymentRes.data.resultMessage : '';
                setResultMessage(paymentResultMessage || '\u00d6deme ba\u015flat\u0131lamad\u0131.');
            }
        } catch (error) {
            console.error("Payment Error:", error);
            setPaymentOutcome('failed');
            setResultMessage("Ödeme servisinde hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Styles matching SportsAppointment.scss
    const styles = {
        sectionShell: {
            maxWidth: '760px',
            margin: '0 auto',
            padding: '34px 28px',
            borderRadius: '22px',
            border: '1px solid #dbe3ef',
            background: 'linear-gradient(145deg, #f8fbff 0%, #eef5ff 45%, #ffffff 100%)',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
        },
        heroWrap: {
            marginBottom: '26px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '14px',
            flexWrap: 'wrap'
        },
        heroTitleWrap: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        heroIcon: {
            width: '46px',
            height: '46px',
            borderRadius: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            background: 'linear-gradient(135deg, #0f75bc 0%, #1d4ed8 100%)',
            boxShadow: '0 8px 18px rgba(29, 78, 216, 0.25)',
            fontSize: '20px'
        },
        title: {
            margin: 0,
            color: '#1e293b',
            fontSize: '1.65rem',
            fontWeight: '750',
            letterSpacing: '-0.01em'
        },
        subtitle: {
            margin: '6px 0 0',
            color: '#64748b',
            fontSize: '0.95rem',
            lineHeight: '1.55'
        },
        secureBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.83rem',
            fontWeight: '700',
            color: '#1e3a8a',
            background: '#dbeafe',
            border: '1px solid #bfdbfe',
            borderRadius: '999px',
            padding: '8px 13px'
        },
        initialCard: {
            background: '#ffffff',
            border: '1px solid #dbe3ef',
            borderRadius: '18px',
            padding: '26px 24px',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)'
        },
        inputGroup: {
            marginBottom: '18px'
        },
        inputV1: {
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #cbd5e1',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#1f2937',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            background: '#fff'
        },
        label: {
            display: 'block',
            fontSize: '13px',
            fontWeight: '700',
            color: '#334155',
            marginBottom: '8px',
            letterSpacing: '0.01em'
        },
        helper: {
            marginTop: '8px',
            fontSize: '12px',
            color: '#64748b'
        },
        startBtn: {
            marginTop: '8px',
            width: '100%',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '700',
            cursor: 'pointer',
            fontSize: '1rem',
            letterSpacing: '0.01em',
            opacity: loading ? 0.7 : 1,
            boxShadow: '0 10px 20px rgba(217, 119, 6, 0.24)'
        },
        submitBtn: {
            width: '100%',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '17px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        }
    };

    return (
        <div className="card-balance-section" style={styles.sectionShell}>
            <div className="sa-facility-details-header sa-unified-tab-header">
                <div className="sa-unified-tab-header-row">
                    <h3 className="sa-unified-tab-title">
                        <span className="sa-unified-tab-icon">
                            <FaCreditCard size={20} />
                        </span>
                        {'Bakiye Y\u00fckleme'}
                    </h3>
                    <span className="sa-unified-tab-pill">
                        <FaLock />
                        {'G\u00fcvenli \u00f6deme'}
                    </span>
                </div>
                <p className="sa-unified-tab-subtitle">
                    {'Kart numaran\u0131z\u0131 ve tutar bilgisini girip \u00f6deme ad\u0131m\u0131na ge\u00e7ebilirsiniz.'}
                </p>
            </div>
            {!showPaymentForm ? (
                // VIEW 1: INITIAL LOAD FORM
                <div className="sa-initial-load-form" style={styles.initialCard}>
                    <div className="form-group" style={styles.inputGroup}>
                        <label style={styles.label}>Yüklenecek Kart Numarası</label>
                        <input 
                            style={styles.inputV1}
                            value={cardNumber} 
                            onChange={e => setCardNumber(e.target.value)} 
                            placeholder="Kart numaranızı giriniz (Örn: 10005)" 
                        />
                    </div>
                    <div className="form-group" style={styles.inputGroup}>
                        <label style={styles.label}>Yüklenecek Tutar (TL)</label>
                        <input 
                            style={styles.inputV1}
                            type="number" 
                            value={amount} 
                            onChange={e => setAmount(e.target.value)} 
                            placeholder="0.00"
                        />
                        <div style={styles.helper}>Minimum tutar işletme kurallarına göre belirlenir.</div>
                    </div>
                    <button 
                        onClick={handleInitiate} 
                        disabled={loading}
                        style={styles.startBtn}>
                        {loading ? 'İşleniyor...' : 'Ödemeye Geç'}
                    </button>
                </div>
            ) : (
                // VIEW 2: PAYMENT CONTAINER (INLINE)
                <div className="sa-inline-payment-container" style={{
                    background: '#fff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                }}>
                    
                    {/* RESULT VIEW */}
                    {paymentOutcome ? (
                        <div style={{ padding: '40px 30px', textAlign: 'center' }}>
                            <div style={{ 
                                fontSize: '60px', 
                                marginBottom: '20px', 
                                color: paymentOutcome === 'success' ? '#22c55e' : '#ef4444' 
                            }}>
                                {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                            </div>
                            <h2 style={{ 
                                fontSize: '24px', 
                                fontWeight: '700', 
                                color: paymentOutcome === 'success' ? '#15803d' : '#b91c1c',
                                marginBottom: '12px'
                            }}>
                                {paymentOutcome === 'success' ? 'Ödeme Başarılı!' : 'Ödeme Başarısız'}
                            </h2>
                            <p style={{ 
                                fontSize: '16px', 
                                color: paymentOutcome === 'success' ? '#166534' : '#991b1b',
                                marginBottom: '30px',
                                maxWidth: '400px',
                                margin: '0 auto 30px'
                            }}>
                                {resultMessage}
                            </p>

                            {paymentOutcome === 'success' && (
                                <div style={{ 
                                    background: '#f0fdf4', 
                                    border: '1px solid #bbf7d0', 
                                    borderRadius: '12px', 
                                    padding: '20px',
                                    marginBottom: '30px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#166534', fontWeight: '600' }}>Kart No</span>
                                        <span style={{ color: '#15803d' }}>{cardNumber}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#166534', fontWeight: '600' }}>Yüklenen Tutar</span>
                                        <span style={{ color: '#15803d', fontWeight: 'bold' }}>{amount} TL</span>
                                    </div>
                                    {transactionId && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#166534', fontWeight: '600' }}>İşlem No</span>
                                            <span style={{ color: '#15803d' }}>{transactionId}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                                {paymentOutcome === 'failed' ? (
                                    <>
                                        <button 
                                            onClick={() => { setPaymentOutcome(null); setPaymentHtml(null); }}
                                            style={{ 
                                                padding: '12px 24px', 
                                                borderRadius: '10px', 
                                                background: '#2563eb', 
                                                color: '#fff', 
                                                border: 'none', 
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Tekrar Dene
                                        </button>
                                        <button 
                                            onClick={() => { setShowPaymentForm(false); setPaymentOutcome(null); }}
                                            style={{ 
                                                padding: '12px 24px', 
                                                borderRadius: '10px', 
                                                background: '#ef4444', 
                                                color: '#fff', 
                                                border: 'none', 
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            iptal Et
                                        </button>
                                    </>
                                ) : (
                                    <button 
                                        onClick={() => { setShowPaymentForm(false); setPaymentOutcome(null); setCardNumber(''); setAmount(''); }}
                                        style={{ 
                                            padding: '12px 24px', 
                                            borderRadius: '10px', 
                                            background: '#2563eb', 
                                            color: '#fff', 
                                            border: 'none', 
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Yeni İşlem Yap
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : paymentHtml ? (
                        // 3D SECURE IFRAME
                        <div style={{ position: 'relative', height: '600px' }}>
                             <div style={{
                                padding: '16px',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#f8fafc'
                            }}>
                                <h4 style={{ margin: 0, fontSize: '16px', color: '#334155' }}>Güvenli Ödeme Ekran?</h4>
                            </div>
                            <iframe
                                ref={iframeRef}
                                title="3D Secure Payment"
                                srcDoc={paymentHtml}
                                onLoad={handleIframeLoad}
                                style={{ width: '100%', height: 'calc(100% - 50px)', border: 'none' }}
                            />
                        </div>
                    ) : (
                        // CREDIT CARD FORM
                        <div style={{ padding: '30px' }}>
                            {/* HEADER */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                marginBottom: '30px',
                                borderBottom: '2px solid #e5e7eb',
                                paddingBottom: '20px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <h4 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>Kart Bilgileri</h4>
                                    <button 
                                        onClick={() => setShowPaymentForm(false)}
                                        style={{ 
                                            border: 'none', 
                                            background: 'transparent', 
                                            color: '#6b7280', 
                                            cursor: 'pointer', 
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        title="Geri Dön"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                </div>
                                <div style={{ 
                                    fontWeight: '700', 
                                    color: '#2563eb', 
                                    fontSize: '16px',
                                    background: '#eff6ff', 
                                    padding: '8px 16px',
                                    borderRadius: '20px'
                                }}>
                                    Toplam: {amount} TL
                                </div>
                            </div>

                            <div className="payment-form-content">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={styles.label}>Kart Numarası</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="0000 0000 0000 0000"
                                            maxLength="19"
                                            value={paymentData.cardNumber}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                const formatted = val.match(/.{1,4}/g)?.join(' ') || val;
                                                if (val.length <= 16) setPaymentData({...paymentData, cardNumber: formatted});
                                                if(paymentErrors.cardNumber) setPaymentErrors({...paymentErrors, cardNumber: ''});
                                            }}
                                            style={{ 
                                                ...styles.inputV1,
                                                borderColor: paymentErrors.cardNumber ? '#ef4444' : '#d1d5db',
                                                paddingRight: '50px' 
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                            onBlur={(e) => e.target.style.borderColor = paymentErrors.cardNumber ? '#ef4444' : '#d1d5db'}
                                        />
                                        <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)' }}>
                                            {detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '1' ? <FaCcVisa size={28} color="#1a1f71" /> :
                                                detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '2' ? <FaCreditCard size={28} color="#F79E1B" /> : 
                                                <FaCreditCard size={24} color="#cbd5e1" />}
                                        </div>
                                    </div>
                                    {paymentErrors.cardNumber && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardNumber}</div>}
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={styles.label}>Kart Üzerindeki İsim</label>
                                    <input
                                        type="text"
                                        placeholder="AD SOYAD"
                                        value={paymentData.cardName}
                                        onChange={e => {
                                            setPaymentData({...paymentData, cardName: e.target.value.toUpperCase()});
                                            if(paymentErrors.cardName) setPaymentErrors({...paymentErrors, cardName: ''});
                                        }}
                                        style={{ 
                                            ...styles.inputV1,
                                            borderColor: paymentErrors.cardName ? '#ef4444' : '#d1d5db'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                        onBlur={(e) => e.target.style.borderColor = paymentErrors.cardName ? '#ef4444' : '#d1d5db'}
                                    />
                                    {paymentErrors.cardName && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardName}</div>}
                                </div>

                                <div style={{ display: 'flex', gap: '20px', marginBottom: '32px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.label}>Son Kullanma Tarihi</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <select 
                                                value={paymentData.expiryMonth}
                                                onChange={e => {
                                                    setPaymentData({...paymentData, expiryMonth: e.target.value});
                                                    if(paymentErrors.expiry) setPaymentErrors({...paymentErrors, expiry: ''});
                                                }}
                                                style={{ 
                                                    ...styles.inputV1,
                                                    flex: 1,
                                                    borderColor: paymentErrors.expiry ? '#ef4444' : '#d1d5db'
                                                }}
                                            >
                                                <option value="">Ay</option>
                                                {Array.from({length: 12}, (_, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{String(i+1).padStart(2,'0')}</option>)}
                                            </select>
                                            <select 
                                                value={paymentData.expiryYear}
                                                onChange={e => {
                                                    setPaymentData({...paymentData, expiryYear: e.target.value});
                                                    if(paymentErrors.expiry) setPaymentErrors({...paymentErrors, expiry: ''});
                                                }}
                                                style={{ 
                                                    ...styles.inputV1,
                                                    flex: 1,
                                                    borderColor: paymentErrors.expiry ? '#ef4444' : '#d1d5db'
                                                }}
                                            >
                                                <option value="">Yıl</option>
                                                {Array.from({length: 15}, (_, i) => <option key={i} value={String(2025+i)}>{2025+i}</option>)}
                                            </select>
                                        </div>
                                        {paymentErrors.expiry && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.expiry}</div>}
                                    </div>

                                    <div style={{ width: '120px' }}>
                                        <label style={styles.label}>CVV</label>
                                        <input
                                            type="text"
                                            placeholder="123"
                                            maxLength="3"
                                            value={paymentData.cvv}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if(val.length <= 3) setPaymentData({...paymentData, cvv: val});
                                                if(paymentErrors.cvv) setPaymentErrors({...paymentErrors, cvv: ''});
                                            }}
                                            style={{ 
                                                ...styles.inputV1,
                                                borderColor: paymentErrors.cvv ? '#ef4444' : '#d1d5db'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                            onBlur={(e) => e.target.style.borderColor = paymentErrors.cvv ? '#ef4444' : '#d1d5db'}
                                        />
                                        {paymentErrors.cvv && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cvv}</div>}
                                    </div>
                                </div>

                                <button 
                                    onClick={handlePaymentSubmit}
                                    disabled={loading}
                                    style={styles.submitBtn}
                                    onMouseOver={(e) => {
                                        if(!loading) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.3)';
                                    }}
                                >
                                    {loading ? 'İşleniyor...' : (
                                        <>
                                            Ödemeyi Tamamla <FaCheckCircle />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CardBalanceLoad;
